import { useCallback, useRef } from 'react';
import jwtDecode from 'jwt-decode';
import { ONE_TAP_SIGN_IN_REJECTED, ONE_TAP_SIGN_IN_SUCCESS, useSocialSignInRequestSuccess, GOOGLE_SIGN_IN_PROVIDER_NAME } from '@dbh/user-area-redux';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import 'lodash/isPlainObject';
import _noop from 'lodash/noop';
import '@dbh/create-custom-react-prop-type';
import createUseBoundEventCreator from '@dbh/create-use-bound-event-creator';
import 'prop-types';

/*
 *
 * Constants: `@dbh/google-one-tap-signin`.
 *
 */// Other constants.
const GOOGLE_ONE_TAP_SIGN_IN_API_URL="https://accounts.google.com/gsi/client";const GOOGLE_ONE_TAP_SIGN_IN_SCRIPT_ID="oneTapSignIn";// After this timeout has expired, we consider the loading of the `Google`
// "one tap" client `JavaScript` failed.
const GOOGLE_ONE_TAP_SIGN_IN_CLIENT_LOAD_TIMEOUT=12e3;const GSI_CLIENT_ID="213834916888-vvujadg3blohtluj4atml0n0kdekvhgq.apps.googleusercontent.com";// Timeout used when we asynchronously load the `google` `API`.

/**
 * Validates the `API` response, in case the request of user credentials was
 * successful. We do not use standard validators, because we would risk sending
 * to `Sentry` the credentials or other sensitive user information (an `API`
 * bug could cause to send the `oneTapSignInResponse` as a serialized `JSON`).
 * @param {Object} source The payload that `window.google.accounts.id.initialize`
 * returned promises resolve to.
 * @param {string} paramName "Calling function" parameter name.
 * @return {Error?} .
 * @throws {Error} If the input is not valid.
 */const oneTapSignInResponsePropType=_noop;

/**
 * Dispatched when it is not possible to perform the `Google one tap`,
 * "one tap" sign in.
 * @param {Error} error .
 * @return {Object} .
 */const oneTapSignInRejected=a=>({type:ONE_TAP_SIGN_IN_REJECTED,payload:{error:a}});const useOneTapSignInRejected=createUseBoundEventCreator(oneTapSignInRejected);/**
 * Dispatched when the user signs in via `Google Smart Lock`, "one tap" sign
 * in `UI`.
 * @param {Object} oneTapSignInResponse Holds the user credentials, the result
 * of the resolved `Promise` from `window.google.accounts.id.initialize` callback.
 * @return {Object} .
 */const oneTapSignInSuccess=a=>({type:ONE_TAP_SIGN_IN_SUCCESS,payload:a});const useOneTapSignInSuccess=createUseBoundEventCreator(oneTapSignInSuccess);

const injectGoogleOneTapSignInScript=withConformsTo("injectGoogleOneTapSignInScript",[],async(a,b)=>{if(document.getElementById(GOOGLE_ONE_TAP_SIGN_IN_SCRIPT_ID))return void(await a());const c=document.createElement("script");c.id=GOOGLE_ONE_TAP_SIGN_IN_SCRIPT_ID,c.onload=a,b&&(c.onerror=b),c.src=GOOGLE_ONE_TAP_SIGN_IN_API_URL,document.head.appendChild(c);});var injectGoogleOneTapSignInScript$1 = injectGoogleOneTapSignInScript;

/*
 *
 * `waitUntilWindowGoogleIsLoaded`: `@dbh/google-one-tap-signin`.
 *
 */const timeInterval=200,maximumTime=1e4,waitUntilWindowGoogleIsLoaded=()=>{let a=0;return new Promise((b,c)=>{const d=window.setInterval(()=>{a>=maximumTime?(clearInterval(d),c(new Error("(`waitUntilWindowGoogleIsLoaded`) Script loaded but initialization "+"timeout (".concat(maximumTime,"ms) elapsed.")))):window.google?(clearInterval(d),b()):a+=timeInterval;},timeInterval);})};/**
 * Resolves only if and when the `window.google` script is truthy, and rejects
 * after the maximum waiting time is elapsed. @TODO `jest` test.
 * @return {Promise} .
 */var waitUntilWindowGoogleIsLoaded$1 = waitUntilWindowGoogleIsLoaded;

const isGoogleWindowTruthy=()=>!!window.google,fnName="useLoadGoogleOneTapSignInApi",useLoadGoogleOneTapSignInApi=()=>{const a=useOneTapSignInSuccess(),b=useOneTapSignInRejected(),c=useSocialSignInRequestSuccess(),d=useCallback(b=>{const{credential:d}=b,e=jwtDecode(d),{email:f}=e;a(e),c({email:f,accessToken:d,signInProvider:GOOGLE_SIGN_IN_PROVIDER_NAME});},[a,c]),e=useRef(!1),f=useCallback(a=>new Promise((c,f)=>{const g=setTimeout(()=>{f(new Error("(`".concat(fnName,"`): client load timeout (").concat(GOOGLE_ONE_TAP_SIGN_IN_CLIENT_LOAD_TIMEOUT,"ms).")));},GOOGLE_ONE_TAP_SIGN_IN_CLIENT_LOAD_TIMEOUT);isGoogleWindowTruthy()&&c(),injectGoogleOneTapSignInScript$1(async()=>{clearTimeout(g);let b=!1;if(!isGoogleWindowTruthy()&&(await new Promise(a=>setTimeout(a,0)),!isGoogleWindowTruthy()&&(await new Promise(a=>setTimeout(a,0)),!isGoogleWindowTruthy())))// `window.google` should be loaded, but when the page is cached,
// it's falsy. To avoid using a global `window.something` `onLoad`
// function, we check many times if it's loaded before giving up.
// Still not loaded, we have to wait more.
try{await waitUntilWindowGoogleIsLoaded$1();}catch(a){b=!0,f(a);}b||(!e.current&&(window.google.accounts.id.initialize({client_id:GSI_CLIENT_ID,auto_select:!0,// eslint-disable-next-line id-blacklist
callback:withConformsTo(fnName,["response",oneTapSignInResponsePropType.isRequired],a=>{d(a);})}),e.current=!0),a&&a(),c());},b);}),[d,b]);return f};/**
 * Asynchronously loads the `Google one tap` client.
 * Note: make sure you are using `dev.daybreakhotels.xyz` while working on
 * "Google one tap".
 * `dev.daybreakhotels.xyz` has been added to be able to use `Google One Tap`
 * while developing, and avoid the unknown origin
 * error. Unfortunately the `Google` dashboard doesn’t accept domains that
 * end in `.localhost`.
 * @see {@link https://console.developers.google.com/apis/credentials/oauthclient/213834916888-vvujadg3blohtluj4atml0n0kdekvhgq.apps.googleusercontent.com?folder=&organizationId=&project=daybreakhotels&supportedpurview=project}
 * @return {Function} Handles script injection and initialization.
 * @see {@link https://developers.google.com/identity/one-tap/web/get-started#load_the_javascript_client_library}
 */var useLoadGoogleOneTapSignInApi$1 = useLoadGoogleOneTapSignInApi;

export { useLoadGoogleOneTapSignInApi$1 as u };
